import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Important = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚡ CSS !important Rule</h1>

      <p className="tutorial-intro">
        The <code>!important</code> rule in CSS is used to give a style property{" "}
        <b>highest priority</b>, overriding normal specificity rules. It should
        be used <b>sparingly</b>, as it can make stylesheets harder to maintain.
      </p>

      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2
          style={{
            color: "#075985",
            marginBottom: "0.5rem",
          }}
        >
          🔑 Key Points
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            <code>!important</code> overrides inline styles, ID selectors, class
            selectors, and element selectors.
          </li>
          <li>
            Use it only when you <b>cannot control the original CSS</b> (e.g.,
            third-party libraries).
          </li>
          <li>
            Overusing it leads to messy, hard-to-debug CSS — use it wisely.
          </li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Without !important</h2>
        <p>
          Normally, the most <b>specific selector</b> wins (ID > class >
          element).
        </p>

        <CodeCopyBlock
          code={`p {
  color: blue;
}
.message {
  color: green;
}
#special {
  color: red;
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <p style={{ color: "blue" }}>Element selector → blue</p>
          <p className="message" style={{ color: "green" }}>
            Class selector → green
          </p>
          <p id="special" style={{ color: "red" }}>
            ID selector → red (wins)
          </p>
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: With !important</h2>
        <p>
          Adding <code>!important</code> makes the property take precedence,
          even over IDs and inline styles.
        </p>

        <CodeCopyBlock
          code={`p {
  color: blue !important;
}
#special {
  color: red;
}
`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <p style={{ color: "blue" }}>
            Blue (forced by <code>!important</code>)
          </p>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 3: Inline Style vs !important
        </h2>
        <p>
          Normally inline styles win, but <code>!important</code> overrides even
          them.
        </p>

        <CodeCopyBlock
          code={`#heading {
  color: green !important;
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <h2 id="heading" style={{ color: "orange" }}>
            This heading is green (overrides inline orange)
          </h2>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 4: Multiple !important Conflicts
        </h2>
        <p>
          If two rules have <code>!important</code>, the one with{" "}
          <b>higher specificity</b> wins.
        </p>

        <CodeCopyBlock
          code={`.note {
  color: blue !important;
}
#priority {
  color: red !important;
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <p className="note" id="priority" style={{ color: "red" }}>
            Red wins (ID + !important)
          </p>
        </div>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Best Practices</h2>
        <ul>
          <li>
            Avoid <code>!important</code> in your main stylesheet.
          </li>
          <li>
            Use it for <b>quick fixes</b> or overriding third-party CSS only.
          </li>
          <li>
            Prefer <b>specific selectors</b> instead of forcing styles.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Learn more about{" "}
          <a href="/css/specificity" className="learn-more">
            CSS Specificity
          </a>{" "}
          to resolve conflicts without relying on <code>!important</code>.
        </p>
      </div>
    </div>
  );
};

export default CSS_Important;
